import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Card, Chip, EmptyState, Skeleton } from '../components/ui'
import { SearchableSelect } from '../components/ui/SearchableSelect'
import { useDevice, useDoors, useEvents, useLocationTree, useSystems, useUpdateDevice } from '../api/hooks'
import { locationNames, locationPaths } from '../lib/locations'
import { LocationLink } from '../components/LocationLink'
import { EventListRow } from '../components/features/EventRows'
import type { Device, Door } from '../api/types'
import { SensorStateChip } from '../components/features/SensorStateChip'
import { CameraBookmarksCard } from '../components/features/CameraBookmarksCard'

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-ostrel-line py-2 last:border-0">
      <span className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
        {label}
      </span>
      <span className="tnum text-right text-sm text-ostrel-ink">{children}</span>
    </div>
  )
}

function PlacementCard({
  device,
  door,
  locationName,
  locationPath,
  locNames,
  locPaths,
  doors,
}: {
  device: Device
  door?: Door
  locationName: string | null
  locationPath: string | null
  locNames: Map<string, string>
  locPaths: Map<string, string>
  doors: Door[]
}) {
  const [editing, setEditing] = useState(false)
  const [locationUid, setLocationUid] = useState(device.location ?? '')
  const [doorUid, setDoorUid] = useState(device.door ?? '')
  const update = useUpdateDevice()

  const doorOptions = useMemo(
    () => doors.map((d) => ({ value: d.unique_id, label: `${d.breadcrumb.join(' / ')} / ${d.name}` })),
    [doors],
  )
  const locationOptions = useMemo(
    () =>
      Array.from(locPaths.entries()).map(([uid, path]) => ({
        value: uid,
        label: locNames.get(uid) ? `${locNames.get(uid)} — ${path}` : path,
      })),
    [locNames, locPaths],
  )

  function save() {
    const body = locationUid ? { location: locationUid } : doorUid ? { door: doorUid } : { location: '' }
    update.mutate(
      { uid: device.unique_id, body },
      {
        onSuccess: () => setEditing(false),
      },
    )
  }

  if (editing) {
    return (
      <Card title="Placement" className="self-start">
        <div className="flex flex-col gap-3">
          <SearchableSelect
            label="Location"
            value={locationUid}
            onChange={(v) => {
              setLocationUid(v)
              if (v) setDoorUid('')
            }}
            options={locationOptions}
            placeholder="— no location —"
            emptyText="No locations"
          />
          <SearchableSelect
            label="Door"
            value={doorUid}
            onChange={(v) => {
              setDoorUid(v)
              if (v) setLocationUid('')
            }}
            options={doorOptions}
            placeholder="— no door —"
            emptyText="No doors"
          />
          {update.isError && (
            <p className="text-xs text-ostrel-danger">{update.error?.message ?? 'Update failed'}</p>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={save}
              disabled={update.isPending}
              className="rounded-sm bg-ostrel-navy px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-ostrel-light hover:bg-ostrel-accent disabled:opacity-60"
            >
              {update.isPending ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => {
                setLocationUid(device.location ?? '')
                setDoorUid(device.door ?? '')
                setEditing(false)
              }}
              disabled={update.isPending}
              className="rounded-sm border border-ostrel-line px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink hover:bg-ostrel-sand disabled:opacity-60"
            >
              Cancel
            </button>
          </div>
        </div>
      </Card>
    )
  }

  return (
    <Card title="Placement" className="self-start">
      {door ? (
        <div className="flex flex-col gap-2">
          <DetailRow label="Door">
            <Link to={`/doors?location=${door.location}`} className="hover:text-ostrel-accent">
              {door.name}
            </Link>
          </DetailRow>
          <DetailRow label="Location">
            <LocationLink uid={door.location}>{door.breadcrumb.join(' / ') || door.location}</LocationLink>
          </DetailRow>
        </div>
      ) : locationPath ? (
        <div className="flex flex-col gap-2">
          <DetailRow label="Location">
            <LocationLink uid={device.location}>{locationName}</LocationLink>
          </DetailRow>
          <DetailRow label="Path">
            <LocationLink uid={device.location}>{locationPath}</LocationLink>
          </DetailRow>
        </div>
      ) : (
        <EmptyState title="Unplaced" hint="This device has no location or door placement." />
      )}
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="mt-3 text-[10px] font-semibold uppercase tracking-widest text-ostrel-gold hover:text-ostrel-accent"
      >
        Edit placement
      </button>
    </Card>
  )
}

export default function DeviceDetailPage() {
  const { uid = '' } = useParams()
  const navigate = useNavigate()
  const device = useDevice(uid)
  const systems = useSystems()
  const doors = useDoors()
  const tree = useLocationTree()
  const [since7d] = useState(() => new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString())
  const events = useEvents({ device: uid, since: since7d })

  const systemNames = new Map((systems.data ?? []).map((s) => [s.unique_id, s.name]))
  const doorById = new Map((doors.data ?? []).map((d) => [d.unique_id, d]))
  const locNames = tree.data ? locationNames(tree.data) : new Map<string, string>()
  const locPaths = tree.data ? locationPaths(tree.data) : new Map<string, string>()

  if (device.isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-5 w-48" />
        <Skeleton className="h-32" />
        <div className="grid gap-4 lg:grid-cols-3">
          <Skeleton className="h-56" />
          <Skeleton className="h-56" />
          <Skeleton className="h-56" />
        </div>
      </div>
    )
  }

  if (device.isError) {
    return (
      <div className="flex flex-col gap-4">
        <p className="tnum flex items-center gap-2 text-[11px] text-ostrel-ink-muted">
          <Link to="/devices" className="hover:text-ostrel-ink">
            ← Devices
          </Link>
          <span>/</span>
          <span className="font-mono">{uid}</span>
        </p>
        <EmptyState
          title="Could not load device"
          hint={device.error.message}
          action={
            <Link
              to="/devices"
              className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-gold hover:text-ostrel-accent"
            >
              ← Back to devices
            </Link>
          }
        />
      </div>
    )
  }

  const d = device.data
  const systemName = systemNames.get(d.system) ?? d.system
  const door = d.door ? doorById.get(d.door) : undefined
  const locationPath = d.location ? (locPaths.get(d.location) ?? d.location) : null
  const locationName = d.location ? (locNames.get(d.location) ?? d.location) : null
  const recentEvents = (events.data?.items ?? []).slice(0, 10)

  return (
    <div className="flex flex-col gap-4">
      <p className="tnum flex items-center gap-2 text-[11px] text-ostrel-ink-muted">
        <Link to="/devices" className="hover:text-ostrel-ink">
          ← Devices
        </Link>
        <span>/</span>
        <span className="uppercase">{d.kind.replace(/_/g, ' ')}</span>
      </p>

      <Card>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-gold">
              {d.kind.replace(/_/g, ' ')} · <span className="tnum font-mono normal-case">{d.unique_id}</span>
            </p>
            <h1 className="mt-1 font-display text-3xl text-ostrel-ink">{d.name}</h1>
            <p className="mt-1 text-[11px] text-ostrel-ink-muted">
              {d.vendor_ref || 'No vendor reference'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Chip
              tone={d.online === null ? 'muted' : d.online ? 'ok' : 'danger'}
              label={d.online === null ? 'unknown' : d.online ? 'online' : 'offline'}
            />
            {d.bound && <Chip tone="info" label="bound" />}
            <SensorStateChip state={d.sensor_state} changedAt={d.sensor_state_changed_at} />
          </div>
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="System" className="self-start">
          <div className="flex items-center justify-between gap-4">
            <span className="text-sm text-ostrel-ink">{systemName}</span>
            <button
              type="button"
              onClick={() => navigate(`/systems/${d.system}`)}
              className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-gold hover:text-ostrel-accent"
            >
              Open system →
            </button>
          </div>
        </Card>

        <PlacementCard
          device={d}
          door={door}
          locationName={locationName}
          locationPath={locationPath}
          locNames={locNames}
          locPaths={locPaths}
          doors={doors.data ?? []}
        />

        <Card title="Details" className="self-start">
          <DetailRow label="Vendor ref">{d.vendor_ref || '—'}</DetailRow>
          <DetailRow label="Kind">{d.kind.replace(/_/g, ' ')}</DetailRow>
          <DetailRow label="Binding">{d.bound ? 'Bound to vendor row' : 'Unbound'}</DetailRow>
        </Card>
      </div>

      {d.kind === 'camera' && <CameraBookmarksCard deviceUid={d.unique_id} />}

      <Card title="Recent events · 7 days">
        {events.isPending ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
          </div>
        ) : events.isError ? (
          <EmptyState title="Could not load events" hint={events.error.message} />
        ) : recentEvents.length === 0 ? (
          <EmptyState title="No recent events" hint="Events from the last 7 days involving this device appear here." />
        ) : (
          <ul className="flex flex-col divide-y divide-ostrel-line">
            {recentEvents.map((e) => (
              <EventListRow key={e.unique_id} event={e} />
            ))}
          </ul>
        )}
      </Card>
    </div>
  )
}
